import type { ClientEvent as ClientEventV3 } from '../proto/v3/client_event';
import type { ClientEvent as ClientEventV2 } from '../proto/v2/client_event';
import type {
    OtherEventOptions,
    ShowEventProps,
    TaskOperations,
    TaskEventOptions,
} from '@/config/types';
import type { DeepPartial } from 'utility-types';

import ShowEvent from './show-event';
import TaskEvent from './task-event';
import CustomStatEvent from './custom-stat-event';
import VideoStatEvent from './video-stat-event';
import { leftPad, getPositiveInteger } from '@/util';
import { getCustomIncrementId, getNomalIncrementId } from '@/util/increment-id';
import Global from '../config/global';

const GMT = (() => {
    const timeZoneGMTOffset = (new Date()).getTimezoneOffset() / 60;
    return timeZoneGMTOffset <= 0
        ? `GMT+${leftPad(-timeZoneGMTOffset + '', 2, '0')}:00`
        : `GMT-${leftPad(timeZoneGMTOffset + '', 2, '0')}:00`;
})();

// 为防止每个传出都带common_package，所以用base保留大多数信息
// 部分信息放到每个消息体内维护，比如client_timestamp
export class ReportEvent {
    // uint64 客户端时间, Event或Stat发生时的时间, 而不是上传时的时间
    client_timestamp = getPositiveInteger(new Date().valueOf());

    // uint64 客户端自增Id, 每新增一条日志+1, 必须是连续的, 用于校检数据完整性
    client_increment_id = 0;

    // string 会话id，设备冷启动或者切到后台超过5分钟再切回前台时，重新生成一个id
    // 端内页面不可缺省
    // Tip: 小程序的global字段不一致
    session_id = Global.sessionId;
    time_zone?: string;
    event_package?: ClientEventV3.EventPackage | ClientEventV2.EventPackage;
    stat_package?: ClientEventV3.CustomEvent | ClientEventV2.VideoStatEvent;
    // 埋点唯一主键， PB 中已不推荐使用
    event_id = '';
    constructor(options: DeepPartial<ReportEvent>, isV2: boolean = false) {
        // v2 没有 time_zone 字段
        if (!isV2) {
            this.time_zone = GMT;
        }
        Object.assign(this, options);
        this.genIncrementId();
    }
    // 设置自增 id，自定义事件和常规事件使用不同的 localStorage key
    genIncrementId() {
        this.client_increment_id = this.isCustomStatEvent() ? getCustomIncrementId() : getNomalIncrementId();
    }
    // 是否是自定义事件
    isCustomStatEvent() {
        return !!(this.stat_package && 'custom_stat_event' in this.stat_package);
    }
    // 获取埋点事件类型
    getEventType() {
        if (this.event_package) {
            const {
                task_event,
                show_event,
                click_event,
                custom_event,
            } = this.event_package as ClientEventV2.EventPackage;
            if (show_event) return 'showEvent';
            if (click_event) return 'clickEvent';
            if (task_event) return 'taskEvent';
            if (custom_event) return 'customEvent';
        }
        return 'customEvent';
    }
}
// 微信小程序事件处理
const StatEvent = (eventOptions: any) => {
    const { currentUrlPackage, referUrlPackage, taskType, ...event } = eventOptions;
    return event;
}

export function logFactory(type: string, data: ShowEventProps | OtherEventOptions, isV2: boolean = false) {
    const { eventId: event_id, currentUrlPackage, referUrlPackage, contentPackage, name, params } = data;
    if (isV2 && type !== 'RADAR' && type !== 'CUSTOM') {
        // v2 需要特殊处理，先保留 ts-ignore
        if (currentUrlPackage) {
            // @ts-ignore
            currentUrlPackage.page2 = currentUrlPackage.page;
            // @ts-ignore
            delete currentUrlPackage.page;
        }
        if (referUrlPackage) {
            // @ts-ignore
            referUrlPackage.page2 = referUrlPackage.page;
            // @ts-ignore
            delete referUrlPackage.page;
        }
    }
    switch (type) {
        case 'PV':
        case 'SHOW':
            return new ReportEvent({
                event_package: ShowEvent({
                    type,
                    status: (data as ShowEventProps).status,
                    currentUrlPackage,
                    referUrlPackage,
                    action: (data as ShowEventProps).type,
                    beginTime: (data as ShowEventProps).beginTime,
                    actionType: (data as ShowEventProps).actionType,
                    name,
                    params,
                    contentPackage,
                    operationDirection: data.operationDirection,
                    auto: (data as ShowEventProps).auto,
                }, isV2),
                event_id,
            }, isV2);
        case 'CUSTOM':
        case 'CUSTOM_STAT_EVENT':
            const customData = CustomStatEvent({
                name,
                params,
                currentUrlPackage,
                referUrlPackage,
            });
            return new ReportEvent(isV2 ?
                {
                    event_package: {
                        custom_event: customData,
                    },
                    event_id,
                }
                :
                {
                    stat_package: {
                        custom_stat_event: customData,
                    },
                    event_id,
                }, isV2);
        case 'RADAR':
            return new ReportEvent({
                stat_package: {
                    custom_stat_event: CustomStatEvent({
                        name,
                        params,
                        currentUrlPackage,
                        referUrlPackage,
                    }),
                },
                event_id,
            }, isV2);
        // 小程序自定义的事件名称
        case 'HEART_BEAT_EVENT':
        case 'LAUNCH_EVENT':
        case 'APP_USAGE_STAT_EVENT':
        case 'EXCEPTION_EVENT':
        case 'DEVICE_STAT_EVENT':
            return new ReportEvent({
                stat_package: {
                    [type.toLowerCase()]: StatEvent(data)
                },
            }, isV2);
        case 'VIDEO':
            if (isV2) {
                return new ReportEvent({
                    stat_package: VideoStatEvent({
                        currentUrlPackage,
                        referUrlPackage,
                        options: params.params,
                    }),
                    event_id,
                }, isV2);
            }
            return new ReportEvent({
                event_package: TaskEvent({
                    type: type as TaskOperations,
                    status: (data as TaskEventOptions).status,
                    taskType: (data as TaskEventOptions).taskType,
                    sessionId: Global.sessionId,
                    currentUrlPackage,
                    referUrlPackage,
                    name,
                    params: params.params,
                    contentPackage,
                }, isV2),
                event_id,
            }, isV2);
        default:
            return new ReportEvent({
                event_package: TaskEvent({
                    type: type as TaskOperations,
                    status: (data as TaskEventOptions).status,
                    taskType: (data as TaskEventOptions).taskType,
                    sessionId: Global.sessionId,
                    currentUrlPackage,
                    referUrlPackage,
                    name,
                    params,
                    contentPackage,
                }, isV2),
                event_id,
            }, isV2);
    }
}
